import {defineStore} from "pinia";

import {
    BasketPromotionsInput,
    CheckoutConfigInput,
    CreateCheckoutResponse,
    ManualDiscountInput,
    ProductVariant,
    Promotion,
    type VariantIdentifier
} from "@/shared/graphql/types";
import {ClientLineItem, clientLineItemIdentity} from "@/shared/services/basket/ClientLineItem";
//@ts-ignore
import basketService from "@/shared/services/basket/basket.service.ts";
import {ClientBasket} from "@/shared/services/basket/ClientBasket.ts";
import {
    BasketAddModification,
    BasketEvent,
    BasketItemEvent,
    BasketQuantity,
    BasketUpdateModification,
    ClientProductVariant,
    cloneItems,
    createItem,
    isBasketUpdateModification,
    waitForBasketReady
} from "@/shared/stores/basket/index.ts";
import {withBusy, withUpdating} from "@/shared/lib/withStatus.ts";

export interface BasketState {
    busy: boolean;
    updating: boolean;
    changed: Date | null;
    lastEvent: BasketEvent | null;
    basket: ClientBasket | null;
}

type PersistResult = {
    event: BasketEvent | null,
    items: ClientLineItem[] | null,
    promotionsInput?: BasketPromotionsInput | null,
}

async function persist(state: BasketState, closure: (basket: ClientBasket) => Promise<PersistResult>) {
    await waitForBasketReady(state)
    return withUpdating(state, async () => {
        const updateBasket = state.basket.clone();
        const action = await closure(updateBasket);

        if (action.items) updateBasket.items = action.items;
        if (action.promotionsInput) updateBasket.promotionsInput = action.promotionsInput;

        const updatedBasket = await basketService.updateBasket(updateBasket);
        state.basket = updatedBasket;
        state.changed = new Date();

        if (action.event !== undefined) state.lastEvent = action.event;
        return updatedBasket;
    });
}

export const useBasketStore = defineStore('basketStore', {
    state: (): BasketState => ({
        busy: false,                   // Any async operation in progress
        updating: false,               // Persist operation ongoing
        changed: null,                 // Last significant change timestamp
        lastEvent: null,               // Last event for UI reactions
        basket: null,                 // The active basket being managed
    }),
    getters: {
        loaded: (state) => !!state.basket,
        basketQuantities: (state) => {
            if (!state.basket) return [];
            return state.basket.items.map((item: ClientLineItem) => ({
                variant: item.identifier.value,
                unit: item.unit.id,
                quantity: item.quantity
            } as BasketQuantity));
        },
        items: (state) => state.basket?.items || [],
        cost: (state) => state.basket?.cost || null,
        valid: (state) => state.basket?.validation?.success || false,
        validation: (state) => state.basket?.validation || null,
        promotions: (state) => state.basket?.promotions || null,
        etag: (state) => state.basket?.etag || null,
        totalItemQuantity: (state) => state.basket?.totalItemQuantity || 0,
        displayVatTotals: (state) => state.basket?.displayVatTotals || false,
        paysVat: (state) => state.basket?.paysVat || false,
    },
    actions: {
        manage(loader: () => Promise<ClientBasket>) {
            return withBusy(this, async () => {
                const basket = await loader()
                if (!basket) throw new Error("Cannot manage a null basket");
                this.basket = basket;
                this.changed = new Date();
                this.lastEvent = new BasketEvent("basket_activated", "");
                return basket
            })
        },

        /**
         * This function is called when we get a socket message of a basket update
         * @param basketId ID of the basket that changed
         * @param etag basket etags are compared to see if changes happened, and we need to reload
         */
        async remoteUpdate(basketId: string, etag: string) {
            if (this.updating) return;  // I am the source of the update, ignore refresh
            if (!this.basket) return;
            if (this.basket.id !== basketId) return;
            if (this.etag === etag) return;

            console.log(`Basket was modified from a different place, updating basket`, etag);
            await withBusy(this, async () => {
                const updatedBasket = await basketService.loadBasket(this.basket.contact.id, this.basket.onBehalfOf);
                this.basket = updatedBasket;
                this.changed = new Date();
                return updatedBasket;
            });
        },

        async applyModifications(variants: BasketAddModification[] | BasketUpdateModification[]) {
            await persist(this, async (basket) => {
                let itemsToUpdate = cloneItems(basket);
                variants.forEach((modification: BasketAddModification | BasketUpdateModification) => {
                    if (isBasketUpdateModification(modification)) {
                        const item = itemsToUpdate.find((item: ClientLineItem) => item.id === modification.id);
                        if (item) { // Update quantity
                            if (modification.quantity === 0) {
                                itemsToUpdate = [...itemsToUpdate.filter((i: ClientLineItem) => i.id !== modification.id)]; // quantity = 0 -> delete
                            } else {
                                item.quantity = modification.quantity;
                            }
                        }
                    } else {
                        pushVariant(itemsToUpdate, modification.variant, modification.quantity, 'add', modification.unit);
                    }
                });
                return {
                    event: new BasketEvent("items_added", variants.length.toString()),
                    items: itemsToUpdate
                };
            });
        },

        async addVariants(requests: BasketAddModification[], mode: string = 'add') {
            await persist(this, async (basket) => {
                let itemsToUpdate = cloneItems(basket);
                for (const request of requests) {
                    pushVariant(itemsToUpdate, request.variant, request.quantity, mode, request.unit);
                }
                let event = new BasketEvent("items_added", requests.length.toString() + " variants")
                if (requests.length === 1) event = new BasketItemEvent("item_added", requests[0].variant.name.toString(), requests[0].variant.code.toString(), requests[0].quantity)
                return {
                    event: event,
                    items: itemsToUpdate,
                };
            });
        },

        async deleteItems(itemIds: string[]) {
            await persist(this, async (basket) => {
                const items = cloneItems(basket);
                const initialItemCount = items.length;
                const updatedItems = items.filter((it: ClientLineItem) => !itemIds.includes(it.id));
                const deletedCount = initialItemCount - updatedItems.length;
                let event = new BasketEvent("items_deleted", deletedCount.toString() + " items")
                if (items.length === 1) event = new BasketItemEvent("item_deleted", items[0].name.toString(), items[0].code.toString(), items[0].quantity)
                return {
                    event: event,
                    items: updatedItems
                };
            });
        },

        async updateQuantity(itemId: string, quantity: number) {
            await persist(this, async (basket) => {
                const items = cloneItems(basket);
                const item: ClientLineItem = items.find((item: ClientLineItem) => item.id === itemId);
                if (item) {
                    item.quantity = quantity;
                    return {
                        event: new BasketItemEvent("item_quantity_updated", item.name.toString(), item.code.toString(), item.quantity),
                        items: items
                    };
                }
                return {event: null, items: null};
            });
        },

        async addRemark(itemId: string, remark: string) {
            await persist(this, async (basket) => {
                const items = cloneItems(basket);
                const item: ClientLineItem = items.find((item: ClientLineItem) => item.id === itemId);
                if (item) {
                    item.remark = remark;
                    return {
                        event: new BasketItemEvent("item_remark_updated", item.name.toString(), item.code.toString(), item.quantity),
                        items: items
                    };
                }
                return {event: null, items: null};
            });
        },

        async addDiscount(itemId: string, discount: ManualDiscountInput) {
            await persist(this, async (basket) => {
                const items = cloneItems(basket);
                const item: ClientLineItem = items.find((item: ClientLineItem) => item.id === itemId);
                if (item) {
                    item.discount = discount;
                    return {
                        event: new BasketItemEvent("item_discount_updated", item.name.toString(), item.code.toString(), item.quantity),
                        items: items
                    };
                }
                return {event: null, items: null};
            });
        },

        async applyPromotion(promotion: Promotion) {
            if (!this.basket) throw new Error("No active basket to apply promotion to.");

            return withUpdating(this, async () => {
                const updatedBasket = await basketService.applyPromotion(this.basket!, promotion);
                this.basket = updatedBasket;
                this.lastEvent = new BasketEvent("promotion_applied", promotion.name || 'Promotion');
                this.changed = new Date();
                return updatedBasket;
            });
        },

        async deletePromotion(promotion: Promotion) {
            if (!this.basket) throw new Error("No active basket to delete promotion from.");

            return withUpdating(this, async () => {
                const updatedBasket = await basketService.deletePromotion(this.basket!, promotion);
                this.basket = updatedBasket;
                this.lastEvent = new BasketEvent("promotion_deleted", promotion.name || 'Promotion');
                this.changed = new Date();
                return updatedBasket;
            });
        },

        async clear() {
            await persist(this, async () => {
                return {
                    event: new BasketEvent("basket_cleared", "Basket"),
                    items: [],
                    promotionsInput: {applied: [], rejected: []} as BasketPromotionsInput
                };
            });
        },

        async checkout(config: CheckoutConfigInput): Promise<CreateCheckoutResponse> {
            if (!this.basket) throw new Error("No active basket to checkout.");

            return withUpdating(this, async () => {
                return basketService.checkout(config, this.basket!);
            });
        },

        async unload() {
            this.basket = null;
            this.changed = null;
            this.lastEvent = null;
        }
    }
});

function pushVariant(
    items: ClientLineItem[],
    variant: ProductVariant | ClientProductVariant,
    quantity: number,
    mode: string,
    unit?: string
): ClientLineItem {
    //Update in a memory basket, ClientLineItem.status = Updating, vue reflected instantly
    const identity = clientLineItemIdentity(
        {
            kind: variant.id ? 'ID' : 'CODE',
            value: variant.id || variant.code,
        } as VariantIdentifier,
        unit
    );

    let item = items.find((item: ClientLineItem) =>
        item.identity === identity && (!unit || unit === item.unit.id)
    );

    // Fallback on code
    if (!item) {
        item = items.find((item: ClientLineItem) =>
            item.code === variant.code && (!unit || unit === item.unit.id)
        );
    }

    if (item) { // Just increase the quantity
        item.quantity = mode === 'add' ? item.quantity + quantity : quantity;
    } else {
        item = createItem(variant, quantity, unit);
        items.push(item);
    }

    return item;
}